import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import React, { useEffect, useRef, useState } from 'react';
import MedicalInsuranceForm, {
  MedicalInsuranceFormRef,
  MedicalInsuranceFormValues,
} from './MedicalInsuranceForm';
import { useGetYTDValueByEmployeeId } from '@nxsys/hooks/api';
import { getTaxWeekTaxYear } from '@nxsys/utils';
import AppLoader from '@nxsys/components/AppLoader';

type CreateMedicalInsuranceProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (values: MedicalInsuranceFormValues) => Promise<void>;
  employeeId: string;
};

const CreateMedicalInsuranceModal: React.FC<CreateMedicalInsuranceProps> = ({
  isOpen,
  toggle,
  onSubmit,
  employeeId,
}: CreateMedicalInsuranceProps) => {
  const { messages } = useTypedIntl();
  const currentTaxWeekTaxYear = getTaxWeekTaxYear();

  const formRef = useRef<MedicalInsuranceFormRef>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (values: MedicalInsuranceFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(values);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const {
    run: getYTDValueByEmployeeId,
    loading: isGetYTDValueByEmployeeIdLoading,
    value: ytdValueByEmployeeIdData,
  } = useGetYTDValueByEmployeeId();

  useEffect(() => {
    if (isOpen && employeeId) {
      getYTDValueByEmployeeId(employeeId, {
        taxYear: currentTaxWeekTaxYear.taxYear,
        taxWeek: currentTaxWeekTaxYear.taxWeek,
      });
    }
  }, [isOpen, employeeId]);

  useEffect(() => {
    if (ytdValueByEmployeeIdData) {
      formRef.current?.setFieldsValue({
        totalEeContribution:
          ytdValueByEmployeeIdData?.data?.accommodationTotalEeContribution?.toString() ||
          '0',
      });
    }
  }, [ytdValueByEmployeeIdData]);

  return (
    <AppModalContainer
      title={messages['common.addNewMedicalInsurance']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => formRef.current?.submit(),
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      {isGetYTDValueByEmployeeIdLoading && <AppLoader />}
      <MedicalInsuranceForm formRef={formRef} onSubmit={handleSubmit} />
    </AppModalContainer>
  );
};

export default CreateMedicalInsuranceModal;
